/**
 * Orchestration des visites guidées (05/10/2026) : parcours « Premiers pas », visite de la page,
 * invitation à la première connexion, démonstration d'appel, avancement enregistré sur le COMPTE.
 * Monté dans BackofficeLayout, hors <Outlet/> : survit aux changements de page.
 *
 * Seules écritures : setVisite (PUT /api/user/preferences, clé `visites`). Seule autre lecture :
 * l'état du verrou de saisie (GET /api/team/daily-log/my-status), pour ne pas inviter par-dessus —
 * et fermer une visite en cours quand le verrou tombe (son voile recouvrirait l'écran de blocage).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import GuidedTour from '../GuidedTour';
import IncomingCallNotification from '../IncomingCallNotification';
import PremiersPasInvite from './PremiersPasInvite';
import { doitProposer } from './regles';
import premiersPas, { CHAPITRES } from '../../tours/premiersPas';
import { preparer } from '../../tours/preparateurs';
import { visitePourRoute } from '../../tours';
import { filtrerEtapes, droitsDepuisAuth, indexDepart } from '../../tours/moteur';
import { useAuth } from '../../hooks/useAuth';
import { usePreferences } from '../../hooks/usePreferences';
import { useOrg } from '../OrgSelector';
import { getEffectiveRole, isUserInternalForOrg } from '../../lib/roles';
import { apiFetch } from '../../lib/api';

const VisiteContext = createContext(null);
// null hors du contrôleur (page testée seule, mise en page autonome) : l'appelant n'affiche alors rien.
export const useVisite = () => useContext(VisiteContext);
// Navigateur piloté (Playwright, Selenium) : les robots de test internes (e2e-crawler-*) ne
// reçoivent jamais la fenêtre plein écran, qui masquerait chaque page qu'ils parcourent.
const estRobot = () => typeof navigator !== 'undefined' && navigator.webdriver === true;

export default function VisiteController({ children }) {
  const { effectiveUser, hasPermission, isImpersonating } = useAuth();
  const { activeOrg } = useOrg();
  const { prefs, prefsChargees, setVisite } = usePreferences();
  const { pathname } = useLocation();
  const [visite, setVisiteActive] = useState(null);       // { id, steps, startIndex }
  const [etape, setEtape] = useState(null);
  // État du verrou de saisie : null = pas encore lu (l'invitation attend), puis faux / vrai.
  const [verrou, setVerrou] = useState(null);
  // Invitation déjà répondue sur cette page. « Plus tard » / « Ne plus proposer » sont écrits sur le
  // compte (setVisite pose `maj` dans le cache aussitôt) ; ceci couvre « Commencer » sur un parcours
  // filtré vide, où rien n'est écrit. Rien dans le stockage de l'onglet.
  const [repondue, setRepondue] = useState(false);

  const orgCode = activeOrg?.code;
  const droits = useMemo(() => droitsDepuisAuth({ user: effectiveUser, hasPermission, orgCode, getEffectiveRole }),
    [effectiveUser, hasPermission, orgCode]);
  const estInterne = !!effectiveUser && isUserInternalForOrg(effectiveUser, orgCode);
  const etatPP = prefs?.visites?.premiers_pas || null;

  // Visite active et sa fin, lues par l'écoute du verrou (posée une fois, au montage).
  const courant = useRef({});

  // Jamais par-dessus le verrou de saisie : on lit l'état une fois (l'invitation attend la réponse,
  // sinon elle s'afficherait un instant avant le blocage), puis on écoute le 423 — et le sondage de
  // DailyLogGuard, qui émet le même événement quand il découvre le verrou (13 h, sans 423).
  // Verrou posé pendant une visite : on la ferme, étape courante gardée pour « Reprendre » (son
  // voile, rendu après l'écran de blocage, le recouvrirait). Fermeture au SIGNAL, pas sur l'état :
  // le verrou levé sans rechargement (journée validée), une visite lancée ensuite doit tenir.
  useEffect(() => {
    if (!estInterne) return undefined;
    let vivant = true;
    const fermerLaVisite = () => {
      const { visite: v, etape: e, surFin: fin } = courant.current;
      if (!v || !fin) return;
      // 423 et sondage peuvent signaler le même verrou coup sur coup : une seule écriture.
      courant.current = { ...courant.current, visite: null };
      fin('verrou', e || v.steps[v.startIndex]);
    };
    apiFetch('/api/team/daily-log/my-status').then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!vivant) return;
        const verrouille = !!(d && d.ok && d.status && d.status.locked);
        setVerrou(verrouille);
        if (verrouille) fermerLaVisite();
      })
      // Lecture impossible : le verrou ne bloque pas l'invitation (son propre écran passe au-dessus).
      .catch(() => { if (vivant) setVerrou(false); });
    const surVerrou = () => { setVerrou(true); fermerLaVisite(); };
    window.addEventListener('daily-log-locked', surVerrou);
    return () => { vivant = false; window.removeEventListener('daily-log-locked', surVerrou); };
  }, [estInterne]);

  const lancerPremiersPas = useCallback(() => {
    setRepondue(true);
    const steps = filtrerEtapes(premiersPas, droits);
    // Aucune étape permise : GuidedTour ne rendrait rien et n'appellerait jamais onComplete.
    if (!steps.length) return;
    const startIndex = etatPP && etatPP.etat === 'en_cours' ? indexDepart(steps, etatPP.etape) : 0;
    setVisiteActive({ id: 'premiers_pas', steps, startIndex });
  }, [droits, etatPP]);

  // Visite de la page courante, filtrée par les droits : ses étapes `permission` n'agissent qu'ici.
  const visitePage = visitePourRoute(pathname);
  const etapesPage = useMemo(() => (visitePage ? filtrerEtapes(visitePage.steps, droits) : []),
    [visitePage, droits]);
  const lancerVisitePage = useCallback(() => {
    if (!visitePage || !etapesPage.length) return;
    setVisiteActive({ id: visitePage.id, steps: etapesPage, startIndex: 0 });
  }, [visitePage, etapesPage]);

  const surEtape = useCallback((step) => {
    setEtape(step);
    if (visite && visite.id === 'premiers_pas') setVisite('premiers_pas', { etat: 'en_cours', etape: step.id });
  }, [visite, setVisite]);

  const surFin = useCallback((raison, step) => {
    if (visite && visite.id === 'premiers_pas') {
      setVisite('premiers_pas', raison === 'termine' ? { etat: 'termine', etape: null } : { etat: 'en_cours', etape: step && step.id });
    } else if (visite) {
      setVisite(visite.id, { etat: raison === 'termine' ? 'termine' : 'en_cours', etape: step && step.id });
    }
    setVisiteActive(null);
    setEtape(null);
  }, [visite, setVisite]);
  courant.current = { visite, etape, surFin };

  const proposer = !visite && doitProposer({
    prefsChargees, estInterne, estSuperadmin: droits.estSuperadmin, usurpe: !!isImpersonating,
    verrouille: verrou !== false, robot: estRobot(), repondue, etat: etatPP,
  });

  const etapePP = etatPP && etatPP.etat === 'en_cours' ? premiersPas.find((s) => s.id === etatPP.etape) : null;
  const chapitreEnCours = (etapePP && CHAPITRES[etapePP.chapitre]) || null;
  const valeur = useMemo(() => ({
    estInterne,
    etatPremiersPas: etatPP,
    chapitreEnCours,
    lancerPremiersPas,
    visitePageDisponible: etapesPage.length > 0,
    lancerVisitePage,
  }), [estInterne, etatPP, chapitreEnCours, lancerPremiersPas, etapesPage, lancerVisitePage]);

  return (
    <VisiteContext.Provider value={valeur}>
      {children}
      {visite && (
        <GuidedTour key={`${visite.id}-${visite.startIndex}`} steps={visite.steps} tourId={visite.id}
          startIndex={visite.startIndex} onEtape={surEtape} onComplete={surFin} preparer={preparer} />
      )}
      {visite && etape && etape.demo === 'appel' && <IncomingCallNotification demo />}
      {proposer && (
        <PremiersPasInvite prenom={(effectiveUser?.name || '').split(' ')[0]}
          onCommencer={lancerPremiersPas}
          onPlusTard={() => { setRepondue(true); setVisite('premiers_pas', { etat: 'plus_tard' }); }}
          onJamais={() => { setRepondue(true); setVisite('premiers_pas', { etat: 'refuse' }); }} />
      )}
    </VisiteContext.Provider>
  );
}
